import { z } from "zod";
/**
 * Primitive closed unions. Every one of these is CLOSED: nothing may be added
 * at runtime, and there is no "OTHER" member. Adding a new value requires
 * editing this file, which is the point.
 */
export const MoveTypeSchema = z.enum([
    "GATHER",
    "INTERVIEW",
    "ANALYSE",
    "PERSUADE",
    "MANOEUVRE",
    "COMMIT",
]);
export const PositionSchema = z.enum(["CONTROLLED", "RISKY", "DESPERATE"]);
export const EffectSchema = z.enum(["LIMITED", "STANDARD", "GREAT"]);
export const BandSchema = z.enum(["GREAT", "SUCCESS", "COST", "MISS"]);
export const ConsequenceTypeSchema = z.enum([
    "REDUCED_EFFECT",
    "COMPLICATION",
    "LOST_OPPORTUNITY",
    "RESOURCE_COST",
    "ESCALATED_POSITION",
    "CLOCK_TICK",
    "NPC_DISPOSITION_SHIFT",
]);
export const FidelitySchema = z.enum(["CLEAN", "STALE", "AGGREGATED", "CONTESTED", "PARTIAL"]);
export const DocumentKindSchema = z.enum([
    "EMAIL",
    "FINANCIAL_TABLE",
    "MEMO",
    "POSTIT",
    "DATAROOM_INDEX",
    "EXHIBIT",
    "TRANSCRIPT",
    "SLIDE",
    /** A lived scene — the site-visit channel (structure-and-rewards phase, section 6). Facts are declared by observation exactly as a document declares facts. */
    "ENVIRONMENT",
]);
/**
 * NOTE (see docs/RECONCILIATION.md): prompt_pack.md P1's OUTPUT JSON literally
 * types `facts[].tier` as `"CORE|COLOUR"` — two values, British spelling, no
 * SUPPORTING tier. This task's own section 1 specifies three values with
 * American spelling. FactTier below follows THIS instruction, since section 1
 * is this phase's direct specification rather than a prompt_pack.md
 * derivation. The prompt_pack.md mismatch is logged for you rather than
 * silently reconciled.
 */
export const FactTierSchema = z.enum(["CORE", "SUPPORTING", "COLOR"]);
export const NpcRelationshipSchema = z.enum([
    "ALLY",
    "MENTOR",
    "GATEKEEPER",
    "ADVERSARY",
    "INFORMANT",
    "SPONSOR",
]);
/**
 * Disposition is an ORDERED enum. DISPOSITION_ORDER is the contract: the
 * index of a value in this array IS its rank, so a caller can only ever move
 * one step along it. No numeric index is exposed anywhere outside this
 * module — consumers compare positions in DISPOSITION_ORDER, never a raw
 * number, keeping "the ordered enum label, not an index" true everywhere
 * downstream (see NarratorView in views.ts).
 */
export const DISPOSITION_ORDER = ["HOSTILE", "GUARDED", "NEUTRAL", "WARM", "CANDID"];
export const DispositionSchema = z.enum(DISPOSITION_ORDER);
/**
 * How a named person is referred to (19C, 2026-10-28). Set on each bible sheet from the case's
 * own prose (`scripts/derive-pronouns.mjs` proposes it with the sentence it was read from, a
 * reader confirms every row, `scripts/set-pronouns.mjs` writes it), so the narrator and the
 * adjudicator are told rather than left to guess from a first name (C3-A1, AW-8). `they` is the
 * choice where a case's prose gives no evidence.
 */
export const NpcPronounsSchema = z.enum(["she", "he", "they"]);
/** The same choice as a model view carries it: the subject and the object form together. */
export const ViewPronounsSchema = z.enum(["she/her", "he/him", "they/them"]);
export const VIEW_PRONOUNS = { she: "she/her", he: "he/him", they: "they/them" };
export const TrackSchema = z.enum([
    "CALIBRATION",
    "FRAMEWORKS",
    "CONFLICT_MANAGEMENT",
    "CREATIVITY",
    "CHAOS",
]);
/**
 * How big a case is (docs/case-authoring/04 §6). Orthogonal to Track: a
 * DRILL is a three-lead, three-beat retry surface; FULL is Harborlight's
 * ~150-object shape. case:check measures the case against the profile's
 * bands; the engine itself is size-agnostic.
 */
export const SizeProfileSchema = z.enum(["FULL", "STANDARD", "DRILL"]);
/**
 * Which politics configuration the case instantiates (docs/case-authoring/02
 * §3): who owns the wrong answer, what contradicting them costs, and who the
 * lower-friction route around them is. P1 is Harborlight's; P7 is Calder &
 * Finch's. Library metadata — never read by the engine at play time.
 */
export const PoliticsSchema = z.enum(["P1", "P2", "P3", "P4", "P5", "P6", "P7"]);
/**
 * WHY the pressure is on at the start (docs/case-authoring/02 §7). The hard-
 * deadline clock and the antagonist clocks dramatise its RISE; this names its
 * SOURCE, so the library can keep consecutive cases from leaning on the same
 * one (the distance rule). Grouped into classes there: investor, creditor,
 * external authority, internal transition, commercial.
 */
export const PressureContextSchema = z.enum([
    "REFINANCING",
    "SALE_PROCESS",
    "ACQUIRER_DILIGENCE",
    "ACTIVIST_INVESTOR",
    "TURNAROUND_LENDER",
    "REGULATOR",
    "LITIGATION",
    "PUBLIC_INCIDENT",
    "FAMILY_SUCCESSION",
    "NEW_LEADER",
    "CUSTOMER_ULTIMATUM",
]);
/** The mechanism families of docs/case-authoring/02 §2, by number. */
export const MECHANISM_FAMILY_COUNT = 15;
export const RubricDimensionSchema = z.enum([
    "STRUCTURE",
    "QUANTITATIVE",
    "JUDGMENT",
    "COMMUNICATION",
    "AMBIGUITY_TOLERANCE",
    "CALIBRATION",
]);
/**
 * The SAME six dimensions in their OTHER representation, and the only
 * sanctioned bridge between the two.
 *
 * There are two forms in play and both are load-bearing: `assessments.dimension`
 * stores the UPPERCASE form (migration 0012's `assessments_dimension_valid`
 * CHECK), while the Assessor's JSON and `Assessment["scores"]` key on the
 * lowercase form. Until now each of the five call sites that needed one of them
 * declared its own private `DIMENSION_KEYS` array — three lowercase, two
 * uppercase — and nothing connected them. apps/api/src/routes/coach.ts looked up
 * `"structure"` against rows the worker had written as `"STRUCTURE"`, the lookup
 * silently missed, and EVERY score the Coach ever saw was null, for every
 * learner, in every session. Reproduced live against a session holding 138
 * scored rows.
 *
 * The pairing is therefore expressed once, here, and derived at the type level
 * via `Uppercase`/`Lowercase` so a future edit that adds a dimension to one form
 * and not the other cannot compile. `rubric-dimension.test.ts` asserts the two
 * sets correspond exactly, in both directions.
 */
export const RUBRIC_DIMENSION_KEYS = [
    "structure",
    "quantitative",
    "judgment",
    "communication",
    "ambiguity_tolerance",
    "calibration",
];
/** Assessment-JSON key -> the form stored in `assessments.dimension`. */
export function storedRubricDimension(key) {
    return key.toUpperCase();
}
/** The form stored in `assessments.dimension` -> Assessment-JSON key. */
export function rubricDimensionKey(dimension) {
    return dimension.toLowerCase();
}
/**
 * THE DIFFICULTY RETOOL (ADR 0020, docs/DIFFICULTY-RETOOL-ASSESSMENT-2026-09-28.md).
 * Closed vocabularies for the engine-owned pricing of stage 0 onward. None of these
 * reaches a model or a learner: they are authored structure the engine reads, and
 * record-only event payloads the debrief and GET /audit disclose.
 */
/** An arc phase's authored difficulty, read by the engine as a DC ceiling (10 / 15 / 20 / 25), never a floor. */
export const ArcDifficultySchema = z.enum(["easy", "moderate", "hard", "very_hard"]);
/** Hamlet's Hit Points (Laws): whether a phase resolves toward hope or fear. Plotted in the debrief only. */
export const StoryBeatSchema = z.enum(["hope", "fear"]);
/** How much a person offers before being asked: a typed summary of their authored `volunteers`. Not warmth. */
export const ForthcomingSchema = z.enum(["OPEN", "MEASURED", "CLOSED"]);
/**
 * The forms of client resistance the engine can price from the shape of an ask
 * (Block, Flawless Consulting): the pause, the whole history, the written purpose,
 * the attack on the number, the referral elsewhere, the demand for the decision.
 */
export const ResistanceFormSchema = z.enum(["SILENCE", "DETAIL", "METHOD", "ATTACK", "DEFLECT", "PRESS"]);
/** Whether a person speaks as freely in company as one-on-one. */
export const SpeakingSettingSchema = z.enum(["anywhere", "private"]);
/**
 * Who was asked for a document relative to who holds it, decided by the engine: the
 * holder (RIGHT), the mentor or an ally who can relay (ADJACENT), anyone else (WRONG),
 * or nobody named (UNKNOWN).
 */
export const CustodySchema = z.enum(["RIGHT", "ADJACENT", "WRONG", "UNKNOWN"]);
/** The engine-derived state of the person a turn deals with: one owner of the person axis per check. */
export const PersonStateSchema = z.enum(["OPEN", "LEVEL", "RESISTING", "PRESSED", "STUNG"]);
/** What the world does after a miss, in the engine's fixed priority order. */
export const RedirectKindSchema = z.enum(["FORCED_ROUTE", "DEAL", "RELAX", "WORLD_MOVES", "ALLY_ASIDE", "ELSEWHERE", "ROOM"]);
/** The published DC ladder. A literal union of exactly these four numbers — not `number`. */
export const DcSchema = z.union([z.literal(10), z.literal(15), z.literal(20), z.literal(25)]);
/**
 * Modifier magnitudes are themselves closed: prompt_pack.md P2 STEP 5 caps
 * each modifier at "+/- 2 to 5". Encoding this as a literal union (rather
 * than `z.number().min(2).max(5)`) means an off-ladder modifier value of, say,
 * 7 or 1 cannot be constructed in the first place, not merely rejected by a
 * range check.
 */
export const ModifierValueSchema = z.union([
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(-2),
    z.literal(-3),
    z.literal(-4),
    z.literal(-5),
]);
/**
 * Reliability tags used throughout for facts and documents (prompt_pack.md P1:
 * `"reliability": "high|medium|contested"`).
 */
export const ReliabilitySchema = z.enum(["high", "medium", "contested"]);
/**
 * The single opaque channel by which clock state may reach the Narrator or
 * the client during play (see NarratorView in views.ts). Never a number.
 */
export const SituationPressureSchema = z.enum(["CALM", "MOUNTING", "ACUTE", "CRITICAL"]);
/**
 * Fact identifiers. Contracts cannot verify true referential integrity
 * against a case bible (that is a database-layer concern per ADR 0003 — a
 * FACT_REVEALED naming an unknown fact_id is rejected by a foreign key, not a
 * Zod schema). What contracts CAN and do enforce is shape: fact ids follow the
 * F### convention used throughout prompt_pack.md's examples (F012, F019,
 * F022...). A malformed id is rejected here; an id that is well-formed but
 * absent from the case bible is caught one layer up, in packages/db.
 */
export const FactIdSchema = z
    .string()
    .regex(/^F\d{3,}$/, "Fact ids must match the pattern F### (e.g. F022)");
//# sourceMappingURL=primitives.js.map